import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FromSchema, JSONSchema } from 'json-schema-to-ts';

import {
  ensureInvestigationLensInitialized,
  getInvestigationLensImplementation,
  toInvestigationLensError,
} from './investigationLens';
import type {
  InvestigationLensState,
  UseInvestigationThroughLensOptions,
  UseInvestigationThroughLensResult,
} from './types';

/**
 * Derives a structured, LLM-generated view of an investigation's report,
 * shaped by a caller-provided JSON Schema. Declarative: generation runs on
 * mount and whenever the schema or investigation changes; results are cached
 * by the Grafana Assistant plugin per (investigation, schema, report
 * content), so remounts don't regenerate for an unchanged report.
 *
 * Field `description`s are per-field instructions to the model — that is the
 * steering mechanism. Deterministic facts (ids, titles, statuses, timestamps,
 * links) are provided to the model verbatim and copied through rather than
 * generated, so reserve schema fields for genuine synthesis from the report.
 *
 * Declare the schema with `as const` to get a fully typed `data` inferred
 * from the schema (via `json-schema-to-ts`):
 *
 * @example
 * ```typescript
 * const schema = {
 *   type: 'object',
 *   properties: {
 *     headline: {
 *       type: 'string',
 *       description: 'One sentence describing the incident for an executive audience, max 80 characters.',
 *     },
 *     followUps: {
 *       type: 'array',
 *       description: 'Concrete follow-up actions to prevent recurrence.',
 *       items: {
 *         type: 'object',
 *         properties: {
 *           action: { type: 'string' },
 *           urgency: { type: 'string', enum: ['now', 'this-week', 'backlog'] },
 *         },
 *         required: ['action', 'urgency'],
 *         additionalProperties: false,
 *       },
 *     },
 *   },
 *   required: ['headline', 'followUps'],
 *   additionalProperties: false,
 * } as const;
 *
 * function IncidentReview({ investigationId }: { investigationId: string }) {
 *   const lens = useInvestigationThroughLens(schema, investigationId, {
 *     origin: 'grafana-irm/incident-review',
 *   });
 *
 *   if (lens.status === 'loading') return <Spinner />;
 *   if (lens.status === 'error') return <span>{lens.error.message}</span>;
 *   // lens.data.followUps[0].urgency is 'now' | 'this-week' | 'backlog'
 *   return <h3>{lens.data.headline}</h3>;
 * }
 * ```
 *
 * Requires the Grafana Assistant plugin; when it is missing or outdated the
 * hook resolves to an `error` state with code `unavailable` — it never throws
 * into the host's render.
 *
 * Note on `enabled` gating: re-enabling serves cached results by default. If
 * the report may have changed while the hook was disabled — e.g. `enabled`
 * gates on the report streaming — set `refreshOnEnable: true` so the fetch
 * triggered by the false → true transition bypasses the caches once.
 */
export function useInvestigationThroughLens<S extends JSONSchema, T = FromSchema<S>>(
  schema: S,
  investigationId: string,
  options: UseInvestigationThroughLensOptions
): UseInvestigationThroughLensResult<T> {
  const { origin, enabled = true, refreshOnEnable = false } = options;
  const [state, setState] = useState<InvestigationLensState<T>>({ status: 'loading' });

  // Key generation on the schema's content, not its identity: consumers
  // typically declare the schema inline, so the object is recreated every
  // render even though it never changes.
  const schemaJson = useMemo(() => JSON.stringify(schema), [schema]);

  // refresh() bumps the generation to re-run generation with a cache bypass.
  // The bypass is one-shot: the ref records which generation already consumed
  // it, so a later run triggered by a schema/investigation change (same
  // generation, different deps) does not inherit `refresh: true`.
  const [generation, setGeneration] = useState({ id: 0, bypassCache: false });
  const consumedGenerationRef = useRef(0);

  // For refreshOnEnable: tracks whether the previous effect run was enabled,
  // so the run triggered by a false → true transition bypasses caches exactly
  // once. Initialized to the mount-time value so a first mount with
  // `enabled: true` takes the normal cached path.
  const wasEnabledRef = useRef(enabled);

  const refresh = useCallback(() => {
    setGeneration((g) => ({ id: g.id + 1, bypassCache: true }));
  }, []);

  useEffect(() => {
    const wasEnabled = wasEnabledRef.current;
    wasEnabledRef.current = enabled;

    if (!enabled) {
      setState({ status: 'loading' });
      return;
    }

    let cancelled = false;
    const bypassFromRefresh = generation.bypassCache && consumedGenerationRef.current !== generation.id;
    consumedGenerationRef.current = generation.id;
    const bypassCache = bypassFromRefresh || (refreshOnEnable && !wasEnabled);
    setState({ status: 'loading' });

    (async () => {
      try {
        await ensureInvestigationLensInitialized();
        const implementation = getInvestigationLensImplementation();
        const data = await implementation({
          investigationId,
          schema: JSON.parse(schemaJson),
          origin,
          refresh: bypassCache,
        });
        if (!cancelled) {
          setState({ status: 'success', data: data as T });
        }
      } catch (err) {
        if (!cancelled) {
          setState({ status: 'error', error: toInvestigationLensError(err) });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [schemaJson, investigationId, origin, enabled, refreshOnEnable, generation]);

  return { ...state, refresh };
}
